import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2, GitMerge, UserX, Users, Mail, Phone, Cake, CalendarClock, ShieldCheck } from "lucide-react";

const REASON_LABEL = { nome: "Stesso nome", telefono: "Stesso telefono", email: "Stessa email" };

// Un singolo gruppo di possibili duplicati con selezione primario/secondari.
function GroupCard({ group, gi, onIgnore, onMergeRequest, busy }) {
  const members = group.members;
  const [primaryId, setPrimaryId] = useState(members[0].id);
  // di default tutti i non-primari sono selezionati per l'unione
  const [selected, setSelected] = useState(() => new Set(members.slice(1).map((m) => m.id)));

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const secondaryIds = members.map((m) => m.id).filter((id) => id !== primaryId && selected.has(id));

  return (
    <Card className="p-4 space-y-3" data-testid={`dup-group-${gi}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Users className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
        <span className="text-sm font-medium">{members.length} profili simili</span>
        {group.matched_on.map((r) => (
          <Badge key={r} variant="secondary" className="text-[10px]">{REASON_LABEL[r] || r}</Badge>
        ))}
      </div>

      <RadioGroup value={primaryId} onValueChange={setPrimaryId} className="space-y-2">
        {members.map((m) => {
          const isPrimary = m.id === primaryId;
          const emails = m.emails && m.emails.length ? m.emails : (m.email ? [m.email] : []);
          return (
            <div key={m.id} data-testid={`dup-member-${m.id}`}
              className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${isPrimary ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold)/0.06)]" : "border-border"}`}>
              <div className="flex items-center gap-2 pt-0.5">
                <RadioGroupItem value={m.id} id={`primary-${m.id}`} data-testid={`dup-primary-${m.id}`} />
                {!isPrimary && (
                  <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggle(m.id)}
                    data-testid={`dup-select-${m.id}`} aria-label="Includi nell'unione" />
                )}
              </div>
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-[hsl(var(--hi-gold-soft))] text-[hsl(var(--hi-gold))] text-xs font-semibold">
                  {initials(`${m.first_name || ""} ${m.last_name || ""}`)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{m.first_name} {m.last_name}</span>
                  {isPrimary && <Badge className="text-[10px] bg-[hsl(var(--hi-gold))] text-white">Da mantenere</Badge>}
                </div>
                <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {emails.map((e) => (
                    <div key={e} className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 shrink-0" />{e}</div>
                  ))}
                  {m.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 shrink-0" />{m.phone}</div>}
                  {m.birthdate && <div className="flex items-center gap-1.5"><Cake className="h-3 w-3 shrink-0" />{m.birthdate}</div>}
                  <div className="flex items-center gap-3 pt-0.5">
                    <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />{m.appointments || 0} appunt.</span>
                    <span>{m.sales || 0} vendite</span>
                    {m.created_at && <span>creato {fmtDate(m.created_at, { day: "2-digit", month: "short", year: "numeric" })}</span>}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive shrink-0"
                disabled={busy} onClick={() => onIgnore(m, members)} data-testid={`dup-not-duplicate-${m.id}`}>
                <UserX className="h-3.5 w-3.5 mr-1" /> Non è un doppione
              </Button>
            </div>
          );
        })}
      </RadioGroup>

      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-muted-foreground">
          {secondaryIds.length > 0 ? `${secondaryIds.length} profilo/i verranno uniti nel principale` : "Seleziona almeno un profilo da unire"}
        </span>
        <Button size="sm" disabled={busy || secondaryIds.length === 0}
          onClick={() => onMergeRequest(primaryId, secondaryIds)} data-testid={`dup-merge-request-${gi}`}>
          <GitMerge className="h-4 w-4 mr-1" /> Anteprima e unisci
        </Button>
      </div>
    </Card>
  );
}

export function DuplicatesDialog({ open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ groups: [], total_groups: 0, total_profiles: 0 });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { primary_id, secondary_ids, preview }

  const load = useCallback(() => {
    setLoading(true);
    api.get("/clients/duplicates")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore nel caricamento dei duplicati"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onIgnore = async (member, members) => {
    const others = members.map((m) => m.id).filter((id) => id !== member.id);
    setBusy(true);
    try {
      await api.post("/clients/duplicates/ignore", { client_id: member.id, others });
      toast.success(`${member.first_name} ${member.last_name} segnalato come non doppione`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const onMergeRequest = async (primary_id, secondary_ids) => {
    setBusy(true);
    try {
      const { data: prev } = await api.get(`/clients/${primary_id}/merge-preview?secondary_ids=${secondary_ids.join(",")}`);
      setConfirm({ primary_id, secondary_ids, preview: prev });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(false); }
  };

  const doMerge = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post(`/clients/${confirm.primary_id}/merge`, { secondary_ids: confirm.secondary_ids });
      toast.success("Profili uniti correttamente");
      setConfirm(null);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore unione"); }
    finally { setBusy(false); }
  };

  const counts = confirm?.preview?.counts || {};
  const activeCounts = Object.entries(counts).filter(([, v]) => v > 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto hi-scroll" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Users className="h-6 w-6 text-[hsl(var(--hi-gold))]" /> Profili da unificare
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div>
          ) : data.total_groups === 0 ? (
            <div className="py-10 text-center" data-testid="duplicates-empty">
              <ShieldCheck className="h-10 w-10 mx-auto text-[hsl(var(--hi-status-success))] mb-2" />
              <p className="font-medium">Nessun doppione rilevato</p>
              <p className="text-sm text-muted-foreground">Tutti i profili risultano unici.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-[hsl(var(--hi-gold-soft))] px-4 py-3 text-sm" data-testid="duplicates-summary">
                Ci sono <b>{data.total_profiles} profili</b> in <b>{data.total_groups} gruppi</b> da controllare.
                Scegli il profilo da mantenere, poi unisci. Se sono omonime diverse, usa <b>“Non è un doppione”</b>.
              </div>
              {data.groups.map((g, gi) => (
                <GroupCard key={gi} group={g} gi={gi} busy={busy} onIgnore={onIgnore} onMergeRequest={onMergeRequest} />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Conferma con anteprima */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="merge-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Conferma unione</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {confirm.secondary_ids.length} profilo/i verranno uniti in <b>{confirm.preview?.primary?.first_name} {confirm.preview?.primary?.last_name}</b>.
                L'operazione è tracciata e sposta tutto lo storico.
              </p>
              <div className="rounded-md border border-border p-3 text-sm">
                <div className="font-medium mb-1">Dati che verranno spostati</div>
                {activeCounts.length === 0 ? (
                  <div className="text-muted-foreground">Nessun dato collegato ai profili da unire</div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                    {activeCounts.map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium text-foreground">{v}</span></div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-md border border-[hsl(var(--hi-gold)/0.4)] bg-[hsl(var(--hi-gold)/0.06)] p-3 text-sm" data-testid="merge-emails-preserved">
                <div className="font-medium mb-1 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email conservate (accesso con ognuna)</div>
                <div className="space-y-0.5 text-muted-foreground">
                  {(confirm.preview?.emails || []).map((e) => <div key={e} className="truncate">{e}</div>)}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Annulla</Button>
            <Button onClick={doMerge} disabled={busy} data-testid="merge-confirm-final">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Unisci profili
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
